/**
 * Category Detector Utility
 * Infers categories from tree structure by analyzing top-level nodes
 */

/**
 * Detect categories from tree structure
 * Categories are typically the direct children of the root node
 * 
 * @param {Object} tree - The tree structure
 * @param {string} rootId - The root node ID (usually "start")
 * @returns {Array} Array of category objects with { id, label, icon, nodes }
 */
export function detectCategories(tree, rootId = 'start') {
  if (!tree || !tree[rootId]) {
    return [];
  }

  const rootNode = tree[rootId];
  const categories = [];

  // Get direct children of root as potential categories
  if (rootNode.children && rootNode.children.length > 0) {
    rootNode.children.forEach((childId) => {
      const childNode = tree[childId];
      if (childNode) {
        // Collect all nodes under this category (including nested children)
        const categoryNodes = collectCategoryNodes(tree, childId);
        
        categories.push({
          id: childId,
          label: childNode.label || childId,
          icon: getCategoryIcon(childNode.label),
          nodes: categoryNodes,
          node: childNode,
        });
      }
    });
  }

  return categories;
}

/**
 * Collect all nodes under a category (recursive)
 * @param {Object} tree - The tree structure
 * @param {string} nodeId - The starting node ID
 * @returns {Array} Array of node IDs in this category
 */
function collectCategoryNodes(tree, nodeId) {
  const nodes = [nodeId];
  const node = tree[nodeId];
  
  if (node && node.children && node.children.length > 0) {
    node.children.forEach((childId) => {
      nodes.push(...collectCategoryNodes(tree, childId));
    });
  }
  
  return nodes;
}

/**
 * Get icon for category based on label
 * @param {string} label - Category label
 * @returns {string} Emoji icon
 */
function getCategoryIcon(label) {
  if (!label) return '📁';
  
  const lowerLabel = label.toLowerCase();
  
  // Category-specific icons
  if (lowerLabel.includes('fundamental') || lowerLabel.includes('basic')) return '🌱';
  if (lowerLabel.includes('strategy') || lowerLabel.includes('tactic')) return '🌿';
  if (lowerLabel.includes('advanced') || lowerLabel.includes('expert')) return '🌳';
  if (lowerLabel.includes('warning') || lowerLabel.includes('avoid')) return '⚠️';
  if (lowerLabel.includes('phase')) return '📘';
  if (lowerLabel.includes('introduction')) return '📖';
  
  return '📁';
}

/**
 * Get category name from node label (cleaned up)
 * @param {string} label - Full node label
 * @returns {string} Clean category name
 */
export function getCategoryName(label) {
  if (!label) return 'Other';
  
  // Remove emojis and common prefixes
  let cleanLabel = label.replace(/[🧱📘🧠💳🚀🛡️🧨]/g, '').trim();
  
  // Remove "PHASE", "SECTION" prefixes
  cleanLabel = cleanLabel.replace(/^(PHASE|Phase|phase|SECTION|Section|section)\s*\d*[:\-]?\s*/i, '');
  
  // Remove leading numbers and dashes
  cleanLabel = cleanLabel.replace(/^\d+[.\-]\s*/, '');
  
  // Convert to uppercase for category headers
  return cleanLabel.toUpperCase() || 'OTHER';
}

export default detectCategories;
